import { FC, useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import {
  ColumnPickerStyled,
  ResetButtonStyled,
  ShowHideColumnToggleWrapStyled,
  TypographyStyled,
} from './ColumnPickerSettings.styled';
import { uploadUserColumnPreferences } from 'api/user';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import Toggle from 'components/Toggle/Toggle';
import { Column } from 'declarations/types/common';
import { useFetchUserColumnPreferences } from 'hooks/api/useUser';

type ColumnPickerSettingsProps = { columns: Column[] };
type ColumnToggleProps = {
  column: Column;
  onToggle: (columnId: string) => void;
  checked: boolean;
};
type ColumnVisibilityMap = { [key: string]: boolean };

const ColumnToggle: FC<ColumnToggleProps> = ({ column, onToggle }) => {
  return (
    <div>
      <FlexContainer>
        <label>{column.label}</label>
        {!column.isRequired && (
          <ShowHideColumnToggleWrapStyled>
            <Toggle
              name={`toggle-${column.id}`}
              defaultChecked={column.visible}
              checked={column.visible}
              onChange={() => onToggle(column.id)}
              key={column.id}
            />
          </ShowHideColumnToggleWrapStyled>
        )}
      </FlexContainer>
    </div>
  );
};

const ColumnPickerSettings: FC<ColumnPickerSettingsProps> = ({ columns }) => {
  const userColumnPreferences = useFetchUserColumnPreferences({
    onSuccess: data => {
      localStorage.setItem('visibleColumns', data);
    },
    onError: err => {
      if (err && err.data?.detail?.startsWith('The specified key does not exist.')) {
        const defaultColumnVisibility: ColumnVisibilityMap = {};
        columns.forEach(column => {
          defaultColumnVisibility[column.id] = true;
        });

        uploadUserColumnPreferences({ RawData: JSON.stringify(columns) });
      }
    },
  });

  const storedVisibleColumnsJSON =
    userColumnPreferences.data ?? localStorage.getItem('visibleColumns');
  const storedVisibleColumns = storedVisibleColumnsJSON
    ? JSON.parse(storedVisibleColumnsJSON)
    : columns;

  const [visibleColumns, setVisibleColumns] = useState<Column[]>(storedVisibleColumns);

  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityMap>(
    columns.reduce((visibilityMap, column) => {
      visibilityMap[column.id] = column.visible;
      return visibilityMap;
    }, {} as ColumnVisibilityMap)
  );

  const getListStyle = (isDraggingOver: boolean) => ({
    background: isDraggingOver ? '#DEE2E6' : '#E8EEFF',
    padding: 16,
    width: '100%',
  });

  const getItemStyle = (isDragging: boolean, draggableStyle: any, columnVisibility: boolean) => {
    const backgroundColor = columnVisibility ? '#F8F9FA' : '#DEE2E6';
    return {
      userSelect: 'none',
      padding: 16,
      margin: `0 0 16px 0`,
      background: isDragging ? '#3F6BDE' : backgroundColor,
      ...draggableStyle,
    };
  };

  const handleColumnToggle = (columnId: string) => {
    setColumnVisibility(prevVisibility => ({
      ...prevVisibility,
      [columnId]: !prevVisibility[columnId],
    }));

    const updatedColumns = visibleColumns.map(column =>
      column.id === columnId ? { ...column, visible: !column.visible } : column
    );
    setVisibleColumns(updatedColumns);
    localStorage.setItem('visibleColumns', JSON.stringify(updatedColumns));
    uploadUserColumnPreferences({ RawData: JSON.stringify(updatedColumns) });
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const reorderedColumns = Array.from(visibleColumns);
    const [reorderedColumn] = reorderedColumns.splice(result.source.index, 1);
    reorderedColumns.splice(result.destination.index, 0, reorderedColumn);

    setVisibleColumns(reorderedColumns);
    localStorage.setItem('visibleColumns', JSON.stringify(reorderedColumns));
    uploadUserColumnPreferences({ RawData: JSON.stringify(reorderedColumns) });
  };

  const handleResetToDefault = () => {
    const defaultColumnVisibility: ColumnVisibilityMap = {};
    columns.forEach(column => {
      defaultColumnVisibility[column.id] = true;
    });
    setColumnVisibility(defaultColumnVisibility);
    setVisibleColumns(columns);
    localStorage.setItem('visibleColumns', JSON.stringify(columns)); // Clear the saved visibility settings
    uploadUserColumnPreferences({ RawData: JSON.stringify(columns) });
  };

  useEffect(() => {
    if (!localStorage.getItem('visibleColumns')) {
      localStorage.setItem('visibleColumns', JSON.stringify(columns));
    }
  }, [columns]);

  return (
    <ColumnPickerStyled>
      <TypographyStyled variant="h4" color="darkBlue">
        Choose Table Columns
        <br /> order and visibility
      </TypographyStyled>
      <ResetButtonStyled onClick={handleResetToDefault}>Reset to Default</ResetButtonStyled>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="columns" direction="vertical">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={getListStyle(snapshot.isDraggingOver)}
            >
              {visibleColumns.map((column, index) => (
                <Draggable key={`key-${column.id}`} draggableId={`drag-${column.id}`} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(
                        snapshot.isDragging,
                        provided.draggableProps.style,
                        column.visible
                      )}
                    >
                      <ColumnToggle
                        column={column}
                        onToggle={handleColumnToggle}
                        checked={columnVisibility[column.id]}
                        key={column.id}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </ColumnPickerStyled>
  );
};

export default ColumnPickerSettings;
